import { useQuery } from '@tanstack/react-query';
import { FileText } from 'lucide-react';
import { api } from '../../lib/api';
import { Card, ErrorNotice, Spinner } from '../../components/ui';

interface Section {
  command: string;
  title: string;
  text: string;
}

/**
 * What the chassis actually holds for this ONU, in its own words — the "OLT
 * config" tab.
 *
 * Every other view of an ONU in this platform is an interpretation — parsed
 * into VLANs, ports, addresses and SSIDs, any of which can be parsed wrongly or
 * not at all. This is the text, and it exists to answer the one question no
 * interpretation can: did what SimpleACS sent actually land? A write reported
 * as applied and a chassis that quietly declined to keep it look identical
 * everywhere else.
 *
 * ZTE answers in three parts and they are kept apart on purpose. The INTERFACE
 * section is what the OLT stores; the LIVE OMCI state is what the unit accepted.
 * When a write did not take, those two disagree — and only seeing both shows it.
 *
 * Secrets are masked server-side. The chassis prints the fleet's inform password
 * verbatim in `tr069-mgmt … password …`.
 */
export function OltRunningConfigView({
  oltId,
  onuId,
  serial,
}: {
  oltId: string;
  onuId: string;
  serial: string;
}) {
  const query = useQuery({
    queryKey: ['onu', onuId, 'running-config'],
    queryFn: () =>
      api.get<{ serial: string; oltName: string; sections: Section[] }>(
        `/olts/${encodeURIComponent(oltId)}/onus/${encodeURIComponent(onuId)}/running-config`,
      ),
    // A CLI session per open. Not refetched on focus: this is a snapshot an
    // operator is reading, and having it change under them mid-comparison is
    // worse than it being a few seconds old.
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <FileText className="size-4 text-muted" />
          On the OLT · {serial}
        </span>
      }
      bodyClassName="space-y-4 p-5"
    >
      <p className="text-xs text-muted">
        What {query.data?.oltName ?? 'the chassis'} holds for this ONU right now, as it holds it.
        Read-only, and passwords are hidden.
      </p>
      {query.isLoading && (
        <div className="flex items-center justify-center gap-3 p-10 text-sm text-muted">
          <Spinner />
          Asking the chassis…
        </div>
      )}
      {query.isError && <ErrorNotice error={query.error} />}
      {query.data?.sections.map((section) => (
        <div key={section.command || section.title}>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="text-xs font-medium text-ink">{section.title}</h3>
            {/* The command itself, so the operator can run it on the chassis and
                compare — this screen is for not being taken on trust. */}
            {section.command && <code className="font-mono text-[11px] text-faint">{section.command}</code>}
          </div>
          {/* A section with no command is the API saying something about the
              chassis (a C650 has no per-ONU live-OMCI read), not its output. */}
          {!section.command ? (
            <p className="mt-1 text-xs text-muted">{section.text}</p>
          ) : section.text.trim() ? (
            <pre className="mt-1 max-h-96 overflow-auto rounded-lg border border-line bg-surface-2 p-3 font-mono text-[11px] leading-relaxed text-ink">
              {section.text}
            </pre>
          ) : (
            <p className="mt-1 rounded-lg border border-line bg-surface-2 p-3 text-xs text-faint">
              The chassis answered nothing here. For the live OMCI state that usually means the ONU
              is offline; for a stored section it means the OLT holds no configuration.
            </p>
          )}
        </div>
      ))}
    </Card>
  );
}
